import { DmpConnectorSettings, DmpVisibility, SecurityLabelEnum } from '@domain/types';
import { Tooltip } from '@honestica/design-system';
import {
  BannerCard,
  Form,
  Icon,
  Input,
  Radio,
  RadioChangeEvent,
  Space,
  Typography,
} from '@honestica/ui-kit/src';
import { tracking } from '@services/analytics';
import { Dispatch, SetStateAction } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './Dmp29SettingsFormItems.module.less';
import { SecretConnectionBlock } from './SecretConnectionBlock.component';

const TITLE_FILED_MAX_LENGTH = 128;

const AVAILABLE_VISIBILITY_OPTIONS = [
  DmpVisibility.VisibleForAll,
  DmpVisibility.HideForProfessionals,
  DmpVisibility.HideForPatient,
];

type Dmp29SettingsProps = {
  originalSettings?: DmpConnectorSettings | undefined;
  settings: DmpConnectorSettings;
  setSettings: Dispatch<SetStateAction<DmpConnectorSettings>>;
  isPatientMinor: boolean;
  isReadOnly: boolean;
};

const computeVisibility = (securityLabels: SecurityLabelEnum[] | undefined): DmpVisibility => {
  if (!securityLabels) {
    return DmpVisibility.VisibleForAll;
  }
  if (securityLabels.includes(SecurityLabelEnum.HideForPatient)) {
    return DmpVisibility.HideForPatient;
  }
  if (securityLabels.includes(SecurityLabelEnum.HideForProfessionals)) {
    return DmpVisibility.HideForProfessionals;
  }
  return DmpVisibility.VisibleForAll;
};

const computePatientVisibilityNoticeVisibility = (
  originalSettings: DmpConnectorSettings | undefined,
  settings: DmpConnectorSettings,
) => {
  if (!originalSettings?.securityLabels?.includes(SecurityLabelEnum.HideForPatient)) return false;

  return !settings?.securityLabels?.includes(SecurityLabelEnum.HideForPatient);
};

const isVisibilityOptionDisabled = (
  visibility: DmpVisibility,
  originalSettings: DmpConnectorSettings | undefined,
  isReadOnly: boolean,
) => {
  if (isReadOnly) {
    return true;
  }

  if (!originalSettings) {
    return false;
  }

  if (visibility === DmpVisibility.HideForPatient) {
    return !originalSettings?.securityLabels?.includes(SecurityLabelEnum.HideForPatient);
  }

  return false;
};

export function Dmp29SettingsFormItems({
  originalSettings,
  settings,
  setSettings,
  isPatientMinor,
  isReadOnly,
}: Dmp29SettingsProps) {
  const { t } = useTranslation();

  const visibility = computeVisibility(settings?.securityLabels);

  const displayPatientVisibilityNotice = computePatientVisibilityNoticeVisibility(
    originalSettings,
    settings,
  );

  const setVisibility = (e: RadioChangeEvent): void => {
    setSettings((s) => {
      const securityLabels: SecurityLabelEnum[] = (s.securityLabels || []).filter(
        (item) =>
          item !== SecurityLabelEnum.HideForPatient &&
          item !== SecurityLabelEnum.HideForProfessionals,
      );
      if (e.target.value === DmpVisibility.HideForPatient) {
        securityLabels.push(SecurityLabelEnum.HideForPatient);
      } else if (e.target.value === DmpVisibility.HideForProfessionals) {
        securityLabels.push(SecurityLabelEnum.HideForProfessionals);
      }

      tracking.trackConnectorSecurityLabelChanged(securityLabels);
      return { ...s, securityLabels };
    });
  };

  return (
    <div className="margin-lg">
      <Form.Item
        htmlFor="documentTitle"
        label={
          <>
            <Typography.Text strong className={styles.docTitleLabel}>
              {t('documents.integrations.dmp.settings.documentTitle')}
            </Typography.Text>
            <Tooltip content={t('documents.integrations.dmp.settings.documentTitleTooltip')}>
              <Icon name="QuestionMarkMinor" size={16} flex />
            </Tooltip>
          </>
        }
      >
        <Input
          aria-label="documentTitle"
          id="documentTitle"
          data-testid="modal-draft-dmp-settings-document-title-input"
          value={settings.documentTitle}
          onChange={(e) => setSettings((s) => ({ ...s, documentTitle: e.target.value }))}
          disabled={isReadOnly}
          maxLength={128}
          status={settings.documentTitle.length >= TITLE_FILED_MAX_LENGTH ? 'warning' : undefined}
        />
        {settings.documentTitle.length >= TITLE_FILED_MAX_LENGTH && (
          <Typography.Text className={styles.warningText}>
            {t('documents.integrations.dmp.settings.documentTitleIsFull')}
          </Typography.Text>
        )}
      </Form.Item>

      {isPatientMinor && (
        <SecretConnectionBlock
          originalSettings={originalSettings}
          settings={settings}
          setSettings={setSettings}
          isReadOnly={isReadOnly}
        />
      )}
      <Form.Item
        htmlFor="visibility"
        label={
          <Typography.Text strong>
            {t('documents.integrations.dmp.settings.confidentiality')}
          </Typography.Text>
        }
      >
        <Radio.Group
          id="visibility"
          value={visibility}
          onChange={setVisibility}
          disabled={isReadOnly}
        >
          <Space direction="vertical">
            {AVAILABLE_VISIBILITY_OPTIONS.map((option) => (
              <Tooltip
                content={
                  isVisibilityOptionDisabled(option, originalSettings, isReadOnly)
                    ? t(`documents.integrations.dmp.visibilityLabelTooltips.${option}`)
                    : ''
                }
              >
                <span className="inline-block">
                  <Radio
                    value={option}
                    key={option}
                    aria-label={option}
                    disabled={isVisibilityOptionDisabled(option, originalSettings, isReadOnly)}
                  >
                    <Trans i18nKey={`documents.integrations.dmp.visibilityLabels.${option}`} />
                  </Radio>
                </span>
              </Tooltip>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>
      {displayPatientVisibilityNotice && (
        <BannerCard
          type="warning"
          iconName="RoadSignWarning"
          title={t('documents.integrations.dmp.notices.patientVisibility.title')}
          message={t('documents.integrations.dmp.notices.patientVisibility.message')}
        />
      )}
    </div>
  );
}
